// src/App.js
import React, { useEffect, useState } from 'react';
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation
} from 'react-router-dom';
import NavBar from './components/NavBar';
import Intro from './pages/Intro';
import JoinRoom from './pages/JoinRoom';      // 새로 추가된 컴포넌트
import SearchRoom from './pages/SearchRoom';
import CreateRoom from './pages/CreateRoom';
import Room from './pages/Room';
import BoardList from './pages/BoardList';
import BoardCreate from './pages/BoardCreate';
import BoardDetail from './pages/BoardDetail';
import MyPage from './pages/MyPage';
import AllRooms from './pages/AllRooms';  // 전체 방 보기 추가
import { db } from './firebaseConfig';
import { doc, getDoc, setDoc } from 'firebase/firestore';

export default function App() {
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // 1) 자동 로그인 복원
  useEffect(() => {
    const stored = localStorage.getItem('userData');
    if (stored) {
      setUser(JSON.parse(stored));
    }
  }, []);

  // 2) 퀴리 기반 자동 입장 (SearchRoom 대신 바로 JoinRoom으로)
  useEffect(() => {
    if (!user) return;
    if (location.pathname.startsWith('/search')) return;
    // 자동 리다이렉트는 join/:roomId/:pw에서 처리
  }, [user, location]);

  // 3) 로그인 핸들러
  const handleLogin = async (userData) => {
    console.log('로그인 시도:', userData);
    
    try {
      // Firestore에서 사용자 정보 확인
      const userRef = doc(db, 'users', userData.id);
      const userSnap = await getDoc(userRef);
      
      let finalUserData = userData;
      
      if (userSnap.exists()) {
        // 기존 사용자면 Firestore의 정보로 업데이트
        console.log('기존 사용자 정보 발견:', userSnap.data());
        finalUserData = {
          ...userData,
          ...userSnap.data(),
          // id는 덮어쓰지 않도록 함
          id: userData.id
        };
        console.log('최종 사용자 정보(기존):', finalUserData);
      } else {
        // 신규 사용자면 Firestore에 정보 저장
        console.log('신규 사용자 등록');
        await setDoc(userRef, {
          id: userData.id,
          nickname: userData.nickname,
          isAnonymous: userData.isAnonymous || false,
          createdAt: new Date()
        });
      }
      
      // 상태 및 로컬스토리지 업데이트
      setUser(finalUserData);
      localStorage.setItem('userData', JSON.stringify(finalUserData));
      
      // 로그인 후 기본 진입 경로
      navigate('/search', { replace: true });
    } catch (error) {
      console.error('로그인 중 오류 발생:', error);
      // 오류가 있어도 기본 로그인은 진행
      setUser(userData);
      localStorage.setItem('userData', JSON.stringify(userData));
      navigate('/search', { replace: true });
    }
  };

  // 4) 로그아웃 핸들러
  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('userData');
    navigate('/login', { replace: true });
  };

  // 5) 닉네임 업데이트 핸들러
  const handleUpdateNickname = newNick => {
    console.log("App에서 닉네임 업데이트:", newNick);
    console.log("기존 사용자 정보:", user);
    const updated = { ...user, nickname: newNick };
    setUser(updated);
    console.log("업데이트된 사용자 정보:", updated);
    localStorage.setItem('userData', JSON.stringify(updated));
    console.log("localStorage 저장 완료");
  };

  // 추가: 아이콘 인덱스 업데이트 핸들러
  const handleUpdateIconIdx = newIconIdx => {
    const updated = { ...user, iconIdx: newIconIdx };
    setUser(updated);
    localStorage.setItem('userData', JSON.stringify(updated));
  };

  // 6) NavBar 표시 여부
  const hideNavOn = ['/login', '/join'];  // join 경로에서는 Nav 숨김
  const showNav = user && !hideNavOn.includes(location.pathname);

  return (
    <>
      {showNav && <NavBar onLogout={handleLogout} />}

      <Routes>
        {/* 1) 루트 접속 → 로그인으로 */}
        <Route path="/" element={<Navigate to="/login" replace />} />

        {/* 2) /login */}
        <Route
          path="/login"
          element={
            user
              ? <Navigate to="/search" replace />
              : <Intro onLogin={handleLogin} />
          }
        />

        {/* 3) /join/:roomId/:pw → JoinRoom */}
        <Route
          path="/join/:roomId/:pw"
          element={
            user
              ? <JoinRoom user={user} />
              : <Intro onLogin={handleLogin} />
          }
        />

        {/* 4) /search */}
        <Route
          path="/search"
          element={
            user
              ? <SearchRoom user={user} />
              : <Intro onLogin={handleLogin} />
          }
        />

        {/* 5) /create */}
        <Route
          path="/create"
          element={
            user
              ? <CreateRoom user={user} />
              : <Navigate to="/login" replace />
          }
        />

        {/* 5-1) /rooms/create */}
        <Route
          path="/rooms/create"
          element={
            user
              ? <CreateRoom user={user} />
              : <Navigate to="/login" replace />
          }
        />

        {/* 6) /rooms/:roomId */}
        <Route
          path="/rooms/:roomId"
          element={
            user
              ? <Room user={user} userData={user} />
              : <Navigate to="/login" replace />
          }
        />

        {/* 7) 게시판 리스트 */}
        <Route
          path="/board"
          element={
            user
              ? <BoardList user={user} userData={user} />
              : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/board/create"
          element={
            user
              ? <BoardCreate user={user} userData={user} />
              : <Navigate to="/login" replace />
          }
        />
        <Route
          path="/board/:postId"
          element={
            user
              ? <BoardDetail user={user} userData={user} />
              : <Navigate to="/login" replace />
          }
        />

        {/* 8) 마이페이지 */}
        <Route
          path="/mypage"
          element={
            user
              ? (
                <MyPage
                  user={user}
                  onLogout={handleLogout}
                  onUpdateNickname={handleUpdateNickname}
                  onUpdateIconIdx={handleUpdateIconIdx}
                />
              )
              : <Navigate to="/login" replace />
          }
        />

        {/* 전체 방 목록 페이지 */}
        <Route
          path="/all-rooms"
          element={
            user
              ? <AllRooms user={user} />
              : <Navigate to="/login" replace />
          }
        />

        {/* 9) 기타 → 로그인 */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </>
  );
}
